import { useMemo, useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Store } from 'components/common/types/Store.types';
import { AsyncSelectLoadDataHandler, SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { CodeNameModel } from 'components/common/types';
import { Advertiser, BrandWithProductCategory, DealStatus } from 'components/common/types/Deal.types';
import { useLoadLookup } from 'components/common/FilterArea/Filters/useLoadLookup';
import { changeMeta } from 'store/dealManagement/reducer';
import { getMetaData } from 'modules/api/deal';
import DataListProps from 'lib/DataList/DataList.types';
import {
  getAllSelectedCreativesPendingStatus,
  getAllSelectedCreativesWithSameDspCode,
} from 'store/pages/contentManagement/creatives/selectors';
import {
  filterDealLookupData,
  getAdvertiserForBrandSelection,
  getBrandForAdvertiserSelection,
} from 'components/pages/ContentManagement/utils/utils';
import {
  loadAdvertisersData,
  loadAgencyLookupData,
  loadBrandsData,
  loadDealIdLookupData,
  loadProductFormatLookupData,
  loadSpecialistLookupData,
} from 'components/pages/ContentManagement/utils/onLoadHandlers';
import { CreativesMetaFormProps } from './CreativesMetaForm.types';

export type UseCreativesMetaFormData = {
  filterLoadedDealIdLookupData: AsyncSelectLoadDataHandler<DataListProps>;
  onLoadAgencyLookupData: AsyncSelectLoadDataHandler<CodeNameModel>;
  onLoadSpecialistLookupData: AsyncSelectLoadDataHandler<CodeNameModel>;
  onLoadProductFormatLookupData: AsyncSelectLoadDataHandler<CodeNameModel>;
  onSelectHandler: SelectHandler<CodeNameModel | DataListProps>;
  onBrandSelect: (dropdownName: string, selectedBrand: BrandWithProductCategory | null) => void;
  onAdvertiserChange: (dropdownName: string, selectedValues: Advertiser | null) => void;
  onLoadBrandsData: AsyncSelectLoadDataHandler<BrandWithProductCategory>;
  onLoadAdvertisersData: AsyncSelectLoadDataHandler<Advertiser>;
  onAssetLookup: AsyncSelectLoadDataHandler<CodeNameModel>;
  defaultSameDspDealOptions: DataListProps[];
  isLoadingSelectedCreativesOptions: boolean;
  productCategoriesOptions: CodeNameModel[];
  areAllSelectedCreativesWithSameDSP: boolean;
};

export type UseCreativesMetaFormDataProps = Pick<
  CreativesMetaFormProps,
  'cancelFunctions' | 'formMetadata' | 'onFormChange'
>;

const useCreativesMetaFormData = ({
  cancelFunctions,
  formMetadata,
  onFormChange,
}: UseCreativesMetaFormDataProps): UseCreativesMetaFormData => {
  const dispatch = useDispatch();
  const areAllSelectedCreativesPendingApproval = useSelector(getAllSelectedCreativesPendingStatus);
  const allSelectedCreativesSameDspCode = useSelector(getAllSelectedCreativesWithSameDspCode);
  const areAllSelectedCreativesPendingWithSameDspCode =
    areAllSelectedCreativesPendingApproval && !!allSelectedCreativesSameDspCode;

  const [isLoadingSelectedCreativesOptions, setIsLoadingSelectedCreativesOptions] = useState(false);
  const [defaultSameDspDealOptions, setDefaultSameDspDealOptions] = useState<DataListProps[]>([]);

  const { onAssetLookup, onLoadBrands } = useLoadLookup(0);
  const meta = useSelector((state: Store) => state.dealManagement.meta);

  const productCategoriesOptions = useMemo(
    () => meta.productCategoryGroups.map((productCategoryGroup) => productCategoryGroup.productCategories!).flat(),
    [meta.productCategoryGroups],
  );

  const onLoadDealIdLookupData = useCallback(
    async (): Promise<DataListProps[]> =>
      loadDealIdLookupData({
        cancelFunctions,
        dspCode: allSelectedCreativesSameDspCode,
        bookingStatusCodes: [DealStatus.PENDING_APPROVAL, DealStatus.APPROVED, DealStatus.LIVE],
      }),
    [allSelectedCreativesSameDspCode],
  );

  const filterLoadedDealIdLookupData = useCallback(
    async (_dropdownName: string, qry: string): Promise<DataListProps[]> =>
      filterDealLookupData(qry, defaultSameDspDealOptions),
    [defaultSameDspDealOptions],
  );

  useEffect(() => {
    if (!areAllSelectedCreativesPendingWithSameDspCode) return;

    const loadDefaultDealOptions = async (): Promise<void> => {
      setIsLoadingSelectedCreativesOptions(true);
      try {
        const data = await onLoadDealIdLookupData();
        if (data) setDefaultSameDspDealOptions(data);
      } finally {
        setIsLoadingSelectedCreativesOptions(false);
      }
    };

    loadDefaultDealOptions();
  }, [areAllSelectedCreativesPendingWithSameDspCode, onLoadDealIdLookupData]);

  const onLoadAgencyLookupData: AsyncSelectLoadDataHandler<CodeNameModel> = async (_dropdownName, query): Promise<[]> =>
    loadAgencyLookupData(cancelFunctions, _dropdownName, query);

  const onLoadSpecialistLookupData: AsyncSelectLoadDataHandler<CodeNameModel> = async (
    _dropdownName,
    query,
  ): Promise<[]> => loadSpecialistLookupData(cancelFunctions, _dropdownName, query);

  const onLoadProductFormatLookupData: AsyncSelectLoadDataHandler<CodeNameModel> = async (
    _dropdownName,
    query,
  ): Promise<[]> => loadProductFormatLookupData(cancelFunctions, _dropdownName, query);

  const onLoadBrandsData: AsyncSelectLoadDataHandler<BrandWithProductCategory> = useCallback(
    (_dropdownName, query) => loadBrandsData(formMetadata.advertiser, onLoadBrands, _dropdownName, query),
    [formMetadata.advertiser, onLoadBrands],
  );

  const onLoadAdvertisersData: AsyncSelectLoadDataHandler<Advertiser> = useCallback(
    (_dropdownName, query) => loadAdvertisersData(formMetadata.brand, onAssetLookup, _dropdownName, query),
    [formMetadata.brand, onAssetLookup],
  );

  const onSelectHandler: SelectHandler<CodeNameModel | DataListProps> = (dropdownName, selectedValue) => {
    onFormChange(dropdownName, selectedValue);
  };

  const onBrandSelect = (dropdownName: string, selectedBrand: BrandWithProductCategory | null): void => {
    onFormChange(dropdownName, selectedBrand);
    onFormChange('productCategory', selectedBrand?.productCategory || null);

    if (selectedBrand === null) {
      onFormChange('advertiser', null);
      return;
    }

    onFormChange('advertiser', getAdvertiserForBrandSelection(selectedBrand, formMetadata.advertiser));
  };

  const onAdvertiserChange = (dropdownName: string, selectedValues: Advertiser | null): void => {
    onFormChange(dropdownName, selectedValues);
    const selectedBrand = getBrandForAdvertiserSelection(selectedValues, selectedValues?.brands, formMetadata.brand);
    onFormChange('brand', selectedBrand);
    onFormChange('productCategory', selectedBrand?.productCategory || null);
  };

  useEffect(() => {
    const getMeta = async (): Promise<void> => {
      try {
        const metaData = await getMetaData({ cancelFunctions });
        dispatch(changeMeta(metaData));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(`Error fetching meta data: ${error}`);
      }
    };

    getMeta();
  }, []);

  return {
    onLoadAgencyLookupData,
    onLoadSpecialistLookupData,
    onLoadProductFormatLookupData,
    onSelectHandler,
    onBrandSelect,
    onAdvertiserChange,
    onLoadBrandsData,
    onLoadAdvertisersData,
    onAssetLookup,
    filterLoadedDealIdLookupData,
    defaultSameDspDealOptions,
    isLoadingSelectedCreativesOptions,
    productCategoriesOptions,
    areAllSelectedCreativesWithSameDSP: !!allSelectedCreativesSameDspCode,
  };
};

export default useCreativesMetaFormData;
